import { OrniTransportError } from "./transport.ts";

export type CloudflareRelayRole = "source" | "client";

export interface CloudflareRelayOptions {
  relayUrl: string;
  role: CloudflareRelayRole;
  token: string;
  epoch: number;
  streamId: string;
  clientId?: string;
  webSocketFactory?: (url: string) => WebSocket;
  connectTimeoutMs?: number;
  heartbeatIntervalMs?: number;
  heartbeatTimeoutMs?: number;
  notifyClose?: boolean;
  onMessage?: (message: CloudflareRelayMessage) => void;
}

export type CloudflareRelayMessage =
  | { type: "hello_ack"; version: string; epoch: number; clientId: string }
  | { type: "open"; streamId: string; target?: Record<string, unknown> }
  | { type: "ack"; streamId: string; sequence: number }
  | { type: "gap"; streamId: string; requestedAfter: number; latest: number }
  | { type: "close"; streamId?: string; reason: string }
  | { type: "frame"; streamId: string; body: Record<string, unknown> }
  | { type: "data"; streamId: string; sequence: number; bytes: Uint8Array };

/**
 * Transport-only Cloudflare adapter. It deliberately does not know what a
 * Provider, Session, prompt, image, or terminal command means. The caller
 * supplies the already-authorized route ticket and owns lifecycle fencing.
 */
export class CloudflareNativeTerminalRelay {
  private readonly options: CloudflareRelayOptions;
  private socket: WebSocket | null = null;
  private sequence = 0;
  private readyTimer: ReturnType<typeof setTimeout> | null = null;
  private readyResolve: (() => void) | null = null;
  private readyReject: ((error: Error) => void) | null = null;
  private suppressCloseNotification = false;
  private heartbeatTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimeoutTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(options: CloudflareRelayOptions) {
    if (!options.relayUrl
      || (!options.token && !(options.role === "client" && isFixedDeviceRelayUrl(options.relayUrl)))
      || !Number.isSafeInteger(options.epoch)) {
      throw new OrniTransportError("INVALID_ARGUMENT", "Cloudflare relay credentials are invalid.");
    }
    if (!options.streamId || new TextEncoder().encode(options.streamId).byteLength > 160) {
      throw new OrniTransportError("INVALID_ARGUMENT", "Cloudflare relay stream id is invalid.");
    }
    this.options = options;
  }

  async connect(): Promise<void> {
    if (this.socket) return;
    const factory = this.options.webSocketFactory ?? ((url: string) => new WebSocket(url));
    const socket = factory(this.options.relayUrl);
    socket.binaryType = "arraybuffer";
    this.socket = socket;
    this.suppressCloseNotification = false;
    const ready = new Promise<void>((resolve, reject) => {
      this.readyResolve = resolve;
      this.readyReject = reject;
    });
    this.readyTimer = setTimeout(() => {
      const error = new OrniTransportError("TIMEOUT", "Cloudflare relay handshake timed out.", true);
      this.readyReject?.(error);
      this.close(1000, "handshake timeout");
    }, Math.max(25, this.options.connectTimeoutMs ?? 10_000));
    socket.onopen = () => {
      socket.send(JSON.stringify({
        type: "hello",
        role: this.options.role,
        token: this.options.token,
        epoch: this.options.epoch,
        ...(this.options.clientId ? { clientId: this.options.clientId } : {}),
      }));
    };
    socket.onmessage = (event) => {
      try {
        if (event.data === HEARTBEAT_RESPONSE) {
          this.acceptHeartbeat();
          return;
        }
        const message = decodeRelayMessage(event.data, this.options.streamId, this.options.role);
        if (message.type === "hello_ack") {
          if (message.epoch !== this.options.epoch) throw new Error("Cloudflare relay epoch is stale.");
          this.clearReadyTimer();
          this.readyResolve?.();
          this.readyResolve = null;
          this.readyReject = null;
          this.scheduleHeartbeat();
        }
        this.options.onMessage?.(message);
      } catch (cause) {
        const error = cause instanceof Error ? cause : new Error("Invalid Cloudflare relay frame.");
        this.readyReject?.(error);
        this.close(1008, error.message);
      }
    };
    socket.onerror = () => {
      const error = new OrniTransportError("UNAVAILABLE", "Cloudflare relay connection failed.", true);
      this.readyReject?.(error);
    };
    socket.onclose = (event) => {
      this.clearReadyTimer();
      this.stopHeartbeat();
      this.socket = null;
      this.readyReject?.(new OrniTransportError("UNAVAILABLE", event.reason || "Cloudflare relay closed.", true));
      this.readyResolve = null;
      this.readyReject = null;
      if (this.options.notifyClose && !this.suppressCloseNotification) {
        this.options.onMessage?.({ type: "close", streamId: this.options.streamId, reason: event.reason || "Cloudflare relay closed." });
      }
    };
    await ready;
    if (this.options.role === "client") this.openStream();
  }

  openStream(target?: Record<string, unknown>): void {
    this.sendJson({ type: "open", streamId: this.options.streamId, ...(target ? { target } : {}) });
  }

  sendFrame(body: Record<string, unknown>): void {
    this.sendJson({ type: "frame", streamId: this.options.streamId, body });
  }

  sendBytes(bytes: Uint8Array): void {
    if (!bytes.byteLength || bytes.byteLength > 64 * 1024) {
      throw new OrniTransportError("INVALID_ARGUMENT", "Cloudflare relay frame is outside the size limit.");
    }
    this.sequence += 1;
    if (!this.socket || this.socket.readyState !== 1) {
      throw new OrniTransportError("UNAVAILABLE", "Cloudflare relay is not connected.", true);
    }
    this.socket.send(encodeDataFrame(this.options.role === "source" ? 1 : 2, this.options.streamId, this.sequence, bytes));
  }

  close(code = 1000, reason = "closed"): void {
    this.clearReadyTimer();
    this.stopHeartbeat();
    this.suppressCloseNotification = true;
    if (this.socket?.readyState === 1) {
      if (this.options.role === "client") {
        this.socket.send(JSON.stringify({ type: "close", streamId: this.options.streamId, reason }));
      }
      this.socket.close(code, reason);
    }
    this.socket = null;
    this.readyResolve = null;
    this.readyReject = null;
  }

  private sendJson(value: Record<string, unknown>): void {
    if (!this.socket || this.socket.readyState !== 1) {
      throw new OrniTransportError("UNAVAILABLE", "Cloudflare relay is not connected.", true);
    }
    const encoded = JSON.stringify(value);
    if (new TextEncoder().encode(encoded).byteLength > MAX_CONTROL_BYTES) {
      throw new OrniTransportError("INVALID_ARGUMENT", "Cloudflare relay control frame is too large.");
    }
    this.socket.send(encoded);
  }

  private clearReadyTimer(): void {
    if (this.readyTimer !== null) clearTimeout(this.readyTimer);
    this.readyTimer = null;
  }

  private scheduleHeartbeat(): void {
    this.stopHeartbeat();
    const interval = Math.max(25, this.options.heartbeatIntervalMs ?? DEFAULT_HEARTBEAT_INTERVAL_MS);
    this.heartbeatTimer = setTimeout(() => {
      this.heartbeatTimer = null;
      const socket = this.socket;
      if (!socket || socket.readyState !== 1) return;
      socket.send(HEARTBEAT_REQUEST);
      const timeout = Math.max(25, this.options.heartbeatTimeoutMs ?? DEFAULT_HEARTBEAT_TIMEOUT_MS);
      this.heartbeatTimeoutTimer = setTimeout(() => {
        this.heartbeatTimeoutTimer = null;
        if (this.socket !== socket || socket.readyState !== 1) return;
        socket.close(4000, "relay heartbeat timeout");
      }, timeout);
      unrefTimer(this.heartbeatTimeoutTimer);
    }, interval);
    unrefTimer(this.heartbeatTimer);
  }

  private acceptHeartbeat(): void {
    if (this.heartbeatTimeoutTimer !== null) clearTimeout(this.heartbeatTimeoutTimer);
    this.heartbeatTimeoutTimer = null;
    this.scheduleHeartbeat();
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer !== null) clearTimeout(this.heartbeatTimer);
    if (this.heartbeatTimeoutTimer !== null) clearTimeout(this.heartbeatTimeoutTimer);
    this.heartbeatTimer = null;
    this.heartbeatTimeoutTimer = null;
  }
}

function isFixedDeviceRelayUrl(value: string): boolean {
  try {
    const url = new URL(value);
    const label = url.hostname.endsWith(".orni.app")
      ? url.hostname.slice(0, -".orni.app".length)
      : "";
    return url.protocol === "wss:"
      && url.port === ""
      && /^[a-z0-9]{18}$/.test(label)
      && url.pathname === "/v1/socket"
      && url.search === ""
      && url.hash === ""
      && url.username === ""
      && url.password === "";
  } catch {
    return false;
  }
}

const HEADER_BYTES = 12;
const MAX_CONTROL_BYTES = 128 * 1024;
const HEARTBEAT_REQUEST = '{"type":"heartbeat"}';
const HEARTBEAT_RESPONSE = '{"type":"heartbeat_ack"}';
const DEFAULT_HEARTBEAT_INTERVAL_MS = 15_000;
const DEFAULT_HEARTBEAT_TIMEOUT_MS = 8_000;
function unrefTimer(timer: ReturnType<typeof setTimeout>): void {
  (timer as unknown as { unref?: () => void }).unref?.();
}
function encodeDataFrame(kind: 1 | 2, streamId: string, sequence: number, bytes: Uint8Array): ArrayBuffer {
  const stream = new TextEncoder().encode(streamId);
  const output = new Uint8Array(HEADER_BYTES + stream.byteLength + bytes.byteLength);
  const view = new DataView(output.buffer);
  view.setUint8(0, kind);
  view.setUint8(1, 1);
  view.setUint16(2, stream.byteLength);
  view.setBigUint64(4, BigInt(sequence));
  output.set(stream, HEADER_BYTES);
  output.set(bytes, HEADER_BYTES + stream.byteLength);
  return output.buffer;
}

function decodeRelayMessage(value: unknown, expectedStreamId: string, role: CloudflareRelayRole): CloudflareRelayMessage {
  if (typeof value !== "string" && !(value instanceof ArrayBuffer)) throw new Error("Unsupported relay message.");
  if (value instanceof ArrayBuffer) {
    if (value.byteLength < HEADER_BYTES) throw new Error("Relay data frame is truncated.");
    const view = new DataView(value);
    const kind = view.getUint8(0);
    const streamLength = view.getUint16(2);
    const expectedKind = role === "source" ? 2 : 1;
    if (kind !== expectedKind || view.getUint8(1) !== 1 || streamLength === 0 || HEADER_BYTES + streamLength >= value.byteLength) {
      throw new Error("Relay data frame is invalid.");
    }
    const streamId = new TextDecoder().decode(new Uint8Array(value, HEADER_BYTES, streamLength));
    if (streamId !== expectedStreamId) throw new Error("Relay stream fence mismatch.");
    const sequence = Number(view.getBigUint64(4));
    return { type: "data", streamId, sequence, bytes: new Uint8Array(value, HEADER_BYTES + streamLength) };
  }
  const parsed = JSON.parse(value) as Record<string, unknown>;
  if (typeof parsed.type !== "string") throw new Error("Relay control frame is invalid.");
  if (parsed.type === "hello_ack" && typeof parsed.version === "string" && Number.isSafeInteger(parsed.epoch) && typeof parsed.clientId === "string") return parsed as unknown as CloudflareRelayMessage;
  if ((parsed.type === "open" || parsed.type === "ack" || parsed.type === "gap" || parsed.type === "frame") && parsed.streamId === expectedStreamId) return parsed as unknown as CloudflareRelayMessage;
  if (parsed.type === "close" && (parsed.streamId === undefined || parsed.streamId === expectedStreamId) && typeof parsed.reason === "string") return parsed as unknown as CloudflareRelayMessage;
  throw new Error("Relay control frame failed stream validation.");
}
